import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { studyPlanAPI, rewardsAPI, quickReviewAPI, lectureAPI, studySessionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { SUBJECTS, ORGAN_SYSTEMS, SUBJECT_COLORS, SYSTEM_COLORS, cn, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    CalendarDays,
    Clock,
    Bell,
    BellRing,
    Plus,
    Trash2,
    Edit,
    BookOpen,
    Target,
    Trophy,
    Star,
    Sparkles,
    PartyPopper,
    ChevronRight,
    X,
    Play,
    Loader2,
    Brain,
    RotateCcw,
    Upload,
    FileText,
    TrendingUp,
    TrendingDown,
    ChevronDown,
    Gauge,
    StickyNote
} from 'lucide-react';
import Layout from '../components/Layout';
import LectureUpload from '../components/LectureUpload';
import ScratchPads from './ScratchPads';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const QUESTION_COUNTS = [5, 10, 15, 20, 25, 30, 50];

// Milestone celebration messages
const MILESTONE_MESSAGES = {
    1000: "🎉 Amazing! You've hit your first 1,000 correct answers!",
    2000: "🌟 Incredible! 2,000 questions mastered!",
    3000: "🔥 On fire! 3,000 correct answers!",
    5000: "💪 Powerhouse! 5,000 questions conquered!",
    10000: "🏆 Legend! 10,000 correct answers!",
    25000: "👑 Royalty! 25,000 questions mastered!",
    50000: "🚀 Unstoppable! 50,000 correct answers!",
    100000: "⭐ ULTIMATE MASTER! 100,000 questions!"
};

export default function StudyPlanner() {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [rewards, setRewards] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showRewardDialog, setShowRewardDialog] = useState(false);
    const [showStudyNowDialog, setShowStudyNowDialog] = useState(false);
    const [selectedPlanForStudy, setSelectedPlanForStudy] = useState(null);
    const [questionCount, setQuestionCount] = useState(10);
    const [startingSession, setStartingSession] = useState(false);
    const [claimingMilestone, setClaimingMilestone] = useState(null);
    const [editingPlan, setEditingPlan] = useState(null);
    const [quickReviewStats, setQuickReviewStats] = useState(null);
    const [predictions, setPredictions] = useState({});
    const [expandedPrediction, setExpandedPrediction] = useState(null);
    const [dailyFocus, setDailyFocus] = useState(null);
    const [planLectures, setPlanLectures] = useState({});
    const [startingLectureSession, setStartingLectureSession] = useState(null);
    const [focusSubject, setFocusSubject] = useState(null);
    const [activeSessions, setActiveSessions] = useState([]);
    const [abandoningSession, setAbandoningSession] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        test_date: null,
        test_date_text: '',
        subjects: [],
        organ_systems: [],
        specific_topics: '',
        target_score: 70,
        notification_enabled: false,
        notification_days: [],
        notification_time: '18:00'
    });

    useEffect(() => {
        fetchData();
        requestNotificationPermission();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [plansRes, rewardsRes, reviewStatsRes, activeSessionsRes] = await Promise.all([
                studyPlanAPI.getAll(),
                rewardsAPI.getStatus(),
                quickReviewAPI.getStats(),
                studySessionAPI.getActive()
            ]);
            setPlans(plansRes.data.plans);
            setRewards(rewardsRes.data);
            setQuickReviewStats(reviewStatsRes.data);
            setActiveSessions(activeSessionsRes.data.sessions || []);

            // Fetch all predictions in a single call (cached)
            const plansList = plansRes.data.plans;
            if (plansList.length > 0) {
                try {
                    const predRes = await studyPlanAPI.getAllPredictions();
                    setPredictions(predRes.data.predictions || {});
                } catch (e) {
                    console.error('Failed to fetch predictions:', e);
                }
            }

            // Fetch daily focus
            try {
                const focusRes = await studyPlanAPI.getDailyFocus();
                setDailyFocus(focusRes.data);
            } catch (e) { /* ignore */ }
            
            // Check for unclaimed milestones
            if (rewardsRes.data.unclaimed_milestones?.length > 0) {
                setShowRewardDialog(true);
            }
        } catch (err) {
            console.error('Failed to fetch data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPlanLectures = async (planId) => {
        if (planLectures[planId]) return;
        try {
            const res = await lectureAPI.getByPlan(planId);
            setPlanLectures(prev => ({ ...prev, [planId]: res.data }));
        } catch (err) {
            console.error('Failed to fetch plan lectures:', err);
        }
    };

    const handlePracticeLectureQuestions = async (planId) => {
        setStartingLectureSession(planId);
        try {
            const res = await lectureAPI.startPlanSession(planId);
            toast.success(`Starting lecture practice (${res.data.total_questions} questions)`);
            navigate(`/lecture-practice/${res.data.session_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start lecture practice');
        } finally {
            setStartingLectureSession(null);
        }
    };

    const handleAbandonSession = async (sessionId) => {
        try {
            await studySessionAPI.abandon(sessionId);
            setActiveSessions(prev => prev.filter(s => s.id !== sessionId));
            toast.success('Session abandoned');
        } catch (err) {
            toast.error('Failed to abandon session');
        } finally {
            setAbandoningSession(null);
        }
    };

    const requestNotificationPermission = async () => {
        if ('Notification' in window && Notification.permission === 'default') {
            await Notification.requestPermission();
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            test_date: null,
            test_date_text: '',
            subjects: [],
            organ_systems: [],
            specific_topics: '',
            target_score: 70,
            notification_enabled: false,
            notification_days: [],
            notification_time: '18:00'
        });
        setEditingPlan(null);
    };

    const handleCreatePlan = async () => {
        if (!formData.name) {
            toast.error('Please enter a plan name');
            return;
        }
        if (!formData.test_date) {
            toast.error('Please enter a valid test date (MM/DD/YYYY)');
            return;
        }

        try {
            const payload = {
                ...formData,
                test_date: formData.test_date ? formData.test_date.toISOString() : null
            };
            delete payload.test_date_text;

            if (editingPlan) {
                await studyPlanAPI.update(editingPlan.id, payload);
                toast.success('Study plan updated!');
            } else {
                await studyPlanAPI.create(payload);
                toast.success('Study plan created!');
            }

            setShowCreateDialog(false);
            resetForm();
            fetchData();

            // Schedule notification if enabled
            if (formData.notification_enabled && 'Notification' in window) {
                scheduleNotifications(formData);
            }
        } catch (err) {
            toast.error('Failed to save study plan');
        }
    };

    const handleDeletePlan = async (planId) => {
        try {
            await studyPlanAPI.delete(planId);
            toast.success('Study plan deleted');
            fetchData();
        } catch (err) {
            toast.error('Failed to delete plan');
        }
    };

    const handleEditPlan = (plan) => {
        setEditingPlan(plan);
        const d = plan.test_date ? new Date(plan.test_date) : null;
        setFormData({
            name: plan.name,
            test_date: d,
            test_date_text: d ? format(d, "MM/dd/yyyy") : '',
            subjects: plan.subjects || [],
            organ_systems: plan.organ_systems || [],
            specific_topics: plan.specific_topics || '',
            target_score: plan.target_score || 70,
            notification_enabled: plan.notification_enabled || false,
            notification_days: plan.notification_days || [],
            notification_time: plan.notification_time || '18:00'
        });
        setShowCreateDialog(true);
    };

    const scheduleNotifications = (plan) => {
        if (Notification.permission === 'granted') {
            toast.success('Study reminders enabled! You\'ll receive desktop notifications.');
        }
    };

    const handleClaimMilestone = async (milestone) => {
        try {
            setClaimingMilestone(milestone);
            const response = await rewardsAPI.claimMilestone(milestone);
            toast.success(response.data.message);
            fetchData();
        } catch (err) {
            toast.error('Failed to claim milestone');
        } finally {
            setClaimingMilestone(null);
        }
    };

    const toggleSubject = (subject) => {
        setFormData(prev => ({
            ...prev,
            subjects: prev.subjects.includes(subject)
                ? prev.subjects.filter(s => s !== subject)
                : [...prev.subjects, subject]
        }));
    };

    const toggleSystem = (system) => {
        setFormData(prev => ({
            ...prev,
            organ_systems: prev.organ_systems.includes(system)
                ? prev.organ_systems.filter(s => s !== system)
                : [...prev.organ_systems, system]
        }));
    };

    const toggleDay = (day) => {
        setFormData(prev => ({
            ...prev,
            notification_days: prev.notification_days.includes(day)
                ? prev.notification_days.filter(d => d !== day)
                : [...prev.notification_days, day]
        }));
    };

    const handleStudyNow = (plan, subject = null) => {
        setSelectedPlanForStudy(plan);
        setFocusSubject(subject);
        setQuestionCount(10);
        setShowStudyNowDialog(true);
    };

    const startStudySession = async () => {
        if (!selectedPlanForStudy) return;
        
        setStartingSession(true);
        try {
            const response = await studyPlanAPI.startSession(selectedPlanForStudy.id, questionCount, focusSubject);
            const label = focusSubject ? `${questionCount} ${focusSubject} questions` : `${questionCount} question study session`;
            toast.success(`Starting ${label}!`);
            setShowStudyNowDialog(false);
            setFocusSubject(null);
            // Navigate to the study session page
            navigate(`/study-session/${response.data.session_id}`);
        } catch (err) {
            console.error('Failed to start session:', err);
            toast.error('Failed to start study session');
        } finally {
            setStartingSession(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Skeleton className="h-48" />
                        <Skeleton className="h-48" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="study-planner-container">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                            Study Planner
                        </h1>
                        <p className="text-slate-500 mt-1">Plan your exams, upload lectures, and track your achievements</p>
                    </div>
                </div>

                {/* Main Tabs */}
                <Tabs defaultValue="plans" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 max-w-lg">
                        <TabsTrigger value="plans" className="flex items-center gap-2">
                            <Target className="h-4 w-4" />
                            Study Plans
                        </TabsTrigger>
                        <TabsTrigger value="lectures" className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Lecture Slides
                        </TabsTrigger>
                        <TabsTrigger value="scratchpad" className="flex items-center gap-2">
                            <StickyNote className="h-4 w-4" />
                            Scratch Pad
                        </TabsTrigger>
                    </TabsList>

                    {/* Study Plans Tab */}
                    <TabsContent value="plans" className="space-y-6 mt-6">
                        {/* Create Plan Button */}
                        <div className="flex justify-end">
                            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} data-testid="create-plan-btn">
                                <Plus className="mr-2 h-4 w-4" />
                                New Study Plan
                            </Button>
                        </div>

                        {/* Continue Session Card */}
                        {activeSessions.length > 0 && (
                            <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50" data-testid="continue-session-card">
                                <CardContent className="p-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                                            <Play className="h-5 w-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-emerald-900">Continue Session</h3>
                                            <p className="text-xs text-emerald-600">You have {activeSessions.length} in-progress session{activeSessions.length !== 1 ? 's' : ''}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {activeSessions.map(s => (
                                            <div key={s.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-white border border-emerald-100 hover:border-emerald-300 transition-colors" data-testid={`active-session-${s.id}`}>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-slate-800 truncate">{s.plan_name}</p>
                                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                                        <span>{s.questions_answered || 0}/{s.question_count} answered</span>
                                                        {s.focus_subject && <Badge variant="outline" className="text-xs border-indigo-200 text-indigo-600">{s.focus_subject}</Badge>}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                                    <Button
                                                        size="sm"
                                                        className="bg-emerald-600 hover:bg-emerald-700"
                                                        onClick={() => navigate(`/study-session/${s.id}`)}
                                                        data-testid={`resume-session-btn-${s.id}`}
                                                    >
                                                        <Play className="h-3.5 w-3.5 mr-1" />
                                                        Resume
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                                                        onClick={() => setAbandoningSession(s.id)}
                                                        data-testid={`abandon-session-btn-${s.id}`}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                                                        End
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Rewards Section */}
                        {rewards && (
                            <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                                <CardContent className="p-6">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                                                <Trophy className="h-8 w-8 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                                                    Study Rewards
                                                </h3>
                                                <p className="text-slate-600">
                                                    <span className="text-2xl font-bold text-amber-600">{rewards.total_correct.toLocaleString()}</span>
                                                    {' '}correct answers
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {rewards.next_milestone && (
                                            <div className="flex-1 max-w-md">
                                                <div className="flex items-center justify-between text-sm mb-2">
                                                    <span className="text-slate-600">Next milestone</span>
                                                    <span className="font-medium text-amber-600">
                                                        {rewards.progress_to_next} / 1,000
                                                    </span>
                                                </div>
                                                <Progress 
                                                    value={(rewards.progress_to_next / 1000) * 100} 
                                                    className="h-3 bg-amber-100"
                                                />
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {1000 - rewards.progress_to_next} more to reach {rewards.next_milestone.toLocaleString()}!
                                                </p>
                                            </div>
                                        )}

                                {rewards.unclaimed_milestones?.length > 0 && (
                                    <Button 
                                        onClick={() => setShowRewardDialog(true)}
                                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                                    >
                                        <PartyPopper className="mr-2 h-4 w-4" />
                                        Claim Rewards ({rewards.unclaimed_milestones.length})
                                    </Button>
                                )}
                            </div>

                            {/* Milestone badges */}
                            <div className="mt-4 flex flex-wrap gap-2">
                                {rewards.all_milestones.map(m => {
                                    const claimed = rewards.claimed_milestones.includes(m);
                                    const reached = rewards.total_correct >= m;
                                    return (
                                        <Badge 
                                            key={m}
                                            variant={claimed ? "default" : "outline"}
                                            className={cn(
                                                "transition-all",
                                                claimed && "bg-amber-500 hover:bg-amber-600",
                                                !claimed && reached && "border-amber-400 text-amber-600 animate-pulse",
                                                !claimed && !reached && "opacity-40"
                                            )}
                                        >
                                            {claimed && <Star className="h-3 w-3 mr-1 fill-current" />}
                                            {m.toLocaleString()}
                                        </Badge>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Quick Review Card */}
                {quickReviewStats && quickReviewStats.total_missed > 0 && (
                    <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                        <CardContent className="p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center shadow-lg">
                                        <Brain className="h-7 w-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                                            Quick Review
                                        </h3>
                                        <p className="text-slate-600">
                                            <span className="text-xl font-bold text-purple-600">{quickReviewStats.total_missed}</span>
                                            {' '}missed questions to review
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex gap-2">
                                    <Button 
                                        variant="outline"
                                        onClick={() => navigate('/weak-areas')}
                                        data-testid="weak-areas-btn"
                                    >
                                        <Target className="mr-2 h-4 w-4" />
                                        View Weak Areas
                                    </Button>
                                    <Button 
                                        onClick={() => navigate('/quick-review')}
                                        className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                                        data-testid="quick-review-btn"
                                    >
                                        <RotateCcw className="mr-2 h-4 w-4" />
                                        Start Review
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Today's Focus Card */}
                {dailyFocus?.focus_items?.length > 0 && (
                    <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50/80 to-purple-50/80" data-testid="daily-focus-card">
                        <CardContent className="py-5 px-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                    <Target className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-indigo-900">Today's Focus</h3>
                                    <p className="text-xs text-indigo-600">Priority subjects across your {dailyFocus.plan_count} study plan{dailyFocus.plan_count !== 1 ? 's' : ''}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {dailyFocus.focus_items.map((item, i) => (
                                    <button
                                        key={item.subject}
                                        onClick={() => {
                                            const plan = plans.find(p => p.id === item.plan_id);
                                            if (plan) handleStudyNow(plan, item.subject);
                                            else toast.error('Study plan not found');
                                        }}
                                        className={cn(
                                            "flex items-start gap-2 px-3 py-2 rounded-lg border text-left transition-all hover:shadow-md hover:scale-[1.02] cursor-pointer",
                                            i === 0 ? "bg-red-50 border-red-200 hover:border-red-400" :
                                            i <= 2 ? "bg-amber-50 border-amber-200 hover:border-amber-400" :
                                            "bg-white border-slate-200 hover:border-slate-400"
                                        )}
                                        data-testid={`focus-item-${i}`}
                                    >
                                        <span className={cn(
                                            "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white mt-0.5",
                                            i === 0 ? "bg-red-500" : i <= 2 ? "bg-amber-500" : "bg-slate-400"
                                        )}>{i + 1}</span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-slate-800 truncate">{item.subject}</p>
                                            <p className="text-xs text-slate-500 truncate">{item.reason}</p>
                                            {item.urgency && (
                                                <p className="text-xs text-red-600 font-medium">{item.urgency}</p>
                                            )}
                                            <p className="text-xs text-slate-400 truncate">{item.plan_name}</p>
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0 mt-0.5" />
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Study Plans */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {plans.map((plan) => (
                        <Card key={plan.id} className={cn(
                            "hover:shadow-lg transition-shadow max-w-lg w-full mx-auto md:mx-0 md:max-w-none",
                            plan.assigned_by_professor
                                ? "border-2 border-red-300 bg-red-50/50"
                                : "border-slate-200"
                        )}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div>
                                        {plan.assigned_by_professor && (
                                            <Badge className="bg-red-100 text-red-700 mb-1.5 text-xs" data-testid={`prof-badge-${plan.id}`}>
                                                Assigned by {plan.professor_name || 'Professor'}
                                            </Badge>
                                        )}
                                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                                            {plan.name}
                                        </CardTitle>
                                        <CardDescription className="flex items-center gap-1 mt-1">
                                            <CalendarDays className="h-4 w-4" />
                                            {plan.test_date ? formatDate(plan.test_date) : "No test date set"}
                                        </CardDescription>
                                    </div>
                                    {plan.days_until_test != null && (
                                    <Badge 
                                        className={cn(
                                            plan.days_until_test <= 7 ? "bg-red-100 text-red-700" :
                                            plan.days_until_test <= 30 ? "bg-amber-100 text-amber-700" :
                                            "bg-emerald-100 text-emerald-700"
                                        )}
                                    >
                                        {plan.days_until_test <= 0 ? 'Today!' : `${plan.days_until_test} days`}
                                    </Badge>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {/* Readiness Prediction Badge */}
                                {(() => {
                                    const pred = predictions[plan.id];
                                    if (!pred || pred.pass_likelihood === 'insufficient_data') return (
                                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
                                            <Gauge className="h-4 w-4 text-slate-400" />
                                            <span className="text-xs text-slate-500">No prediction data yet — practice to see your readiness</span>
                                        </div>
                                    );
                                    const colors = {
                                        high: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
                                        moderate: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', bar: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700' },
                                        low: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700' },
                                        very_low: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', bar: 'bg-red-500', badge: 'bg-red-100 text-red-700' },
                                    };
                                    const c = colors[pred.pass_likelihood] || colors.very_low;
                                    const isExpanded = expandedPrediction === plan.id;
                                    return (
                                        <div className={cn("rounded-lg border", c.bg, c.border)}>
                                            <button
                                                onClick={() => {
                                                    const next = isExpanded ? null : plan.id;
                                                    setExpandedPrediction(next);
                                                    if (next) fetchPlanLectures(next);
                                                }}
                                                className="w-full flex items-center justify-between px-3 py-2"
                                                data-testid={`prediction-toggle-${plan.id}`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Gauge className={cn("h-4 w-4", c.text)} />
                                                    <span className={cn("text-sm font-semibold", c.text)}>{pred.pass_label}</span>
                                                    <Badge className={cn("text-xs", c.badge)}>{pred.readiness_score}%</Badge>
                                                </div>
                                                <ChevronDown className={cn("h-4 w-4 transition-transform", c.text, isExpanded && "rotate-180")} />
                                            </button>
                                            {isExpanded && (
                                                <div className="px-3 pb-3 space-y-3">
                                                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                                                        <div className={cn("h-full rounded-full transition-all", c.bar)} style={{ width: `${pred.readiness_score}%` }} />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                                        <div className="flex justify-between"><span className="text-slate-500">Accuracy</span><span className="font-medium">{pred.overall_accuracy}%</span></div>
                                                        <div className="flex justify-between"><span className="text-slate-500">Questions</span><span className="font-medium">{pred.total_questions}</span></div>
                                                        <div className="flex justify-between"><span className="text-slate-500">Target</span><span className="font-medium">{pred.target_score}%</span></div>
                                                        <div className="flex justify-between">
                                                            <span className="text-slate-500">Trend</span>
                                                            <span className={cn("font-medium flex items-center gap-0.5", pred.trend > 0 ? "text-emerald-600" : pred.trend < 0 ? "text-red-600" : "text-slate-600")}>
                                                                {pred.trend > 0 ? <TrendingUp className="h-3 w-3" /> : pred.trend < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                                                                {pred.trend > 0 ? '+' : ''}{pred.trend}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {pred.weak_areas?.length > 0 && (
                                                        <div>
                                                            <p className="text-xs font-medium text-red-600 mb-1">Weak Areas</p>
                                                            <div className="flex flex-wrap gap-1">
                                                                {pred.weak_areas.map(w => (
                                                                    <Badge key={w.subject} variant="outline" className="text-xs border-red-200 text-red-600">{w.subject} ({w.accuracy}%)</Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {pred.strong_areas?.length > 0 && (
                                                        <div>
                                                            <p className="text-xs font-medium text-emerald-600 mb-1">Strong Areas</p>
                                                            <div className="flex flex-wrap gap-1">
                                                                {pred.strong_areas.map(s => (
                                                                    <Badge key={s.subject} variant="outline" className="text-xs border-emerald-200 text-emerald-600">{s.subject} ({s.accuracy}%)</Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <p className="text-xs text-slate-400">Coverage: {pred.coverage_percent}% of plan topics practiced</p>
                                                    {pred.recommendations?.length > 0 && (
                                                        <div className="mt-2 pt-2 border-t border-slate-200">
                                                            <p className="text-xs font-semibold text-indigo-700 mb-1.5">Focus On</p>
                                                            <div className="space-y-1">
                                                                {pred.recommendations.slice(0, 4).map((rec, i) => (
                                                                    <div key={rec.subject} className="flex items-center gap-2 text-xs">
                                                                        <span className={cn(
                                                                            "w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0",
                                                                            i === 0 ? "bg-red-500" : i === 1 ? "bg-amber-500" : "bg-slate-400"
                                                                        )}>{i + 1}</span>
                                                                        <span className="font-medium text-slate-700">{rec.subject}</span>
                                                                        <span className="text-slate-400 truncate">{rec.reason}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {/* Linked Lecture Content */}
                                                    {(() => {
                                                        const lectureData = planLectures[plan.id];
                                                        if (!lectureData || lectureData.uploads?.length === 0) return null;
                                                        return (
                                                            <div className="mt-2 pt-2 border-t border-slate-200" data-testid={`plan-lectures-${plan.id}`}>
                                                                <p className="text-xs font-semibold text-purple-700 mb-1.5 flex items-center gap-1">
                                                                    <FileText className="h-3 w-3" />
                                                                    Lecture Content ({lectureData.total_questions} questions)
                                                                </p>
                                                                <div className="space-y-1">
                                                                    {lectureData.uploads.map(u => (
                                                                        <div key={u.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-purple-50 border border-purple-100">
                                                                            <div className="flex items-center gap-1.5 min-w-0">
                                                                                <FileText className="h-3 w-3 text-purple-500 flex-shrink-0" />
                                                                                <span className="truncate text-slate-700">{u.filename}</span>
                                                                            </div>
                                                                            <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-600 flex-shrink-0 ml-2">
                                                                                {u.questions_generated || 0} Qs
                                                                            </Badge>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {lectureData.total_questions > 0 && (
                                                                    <Button
                                                                        size="sm"
                                                                        className="w-full mt-2 bg-purple-600 hover:bg-purple-700 text-xs h-7"
                                                                        onClick={() => handlePracticeLectureQuestions(plan.id)}
                                                                        disabled={startingLectureSession === plan.id}
                                                                        data-testid={`practice-lecture-btn-${plan.id}`}
                                                                    >
                                                                        {startingLectureSession === plan.id ? (
                                                                            <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Starting...</>
                                                                        ) : (
                                                                            <><Play className="h-3 w-3 mr-1" /> Practice Lecture Questions</>
                                                                        )}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Subjects */}
                                {plan.subjects?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 mb-1">Subjects</p>
                                        <div className="flex flex-wrap gap-1">
                                            {plan.subjects.map(s => (
                                                <Badge key={s} variant="outline" className="text-xs">
                                                    {s}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Systems */}
                                {plan.organ_systems?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 mb-1">Systems</p>
                                        <div className="flex flex-wrap gap-1">
                                            {plan.organ_systems.map(s => (
                                                <Badge key={s} variant="outline" className="text-xs">
                                                    {s}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Topics */}
                                {plan.specific_topics && (
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 mb-1">Topics</p>
                                        <p className="text-sm text-slate-600 line-clamp-2">{plan.specific_topics}</p>
                                    </div>
                                )}

                                {/* Notification status */}
                                {plan.notification_enabled && (
                                    <div className="flex items-center gap-2 text-sm text-indigo-600">
                                        <BellRing className="h-4 w-4" />
                                        <span>{plan.notification_days?.join(', ')} at {plan.notification_time}</span>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex flex-col gap-2 pt-2">
                                    {/* Study Now Button - Primary Action */}
                                    <Button 
                                        size="sm"
                                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                                        onClick={() => handleStudyNow(plan)}
                                        data-testid={`study-now-btn-${plan.id}`}
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        Study Now
                                    </Button>
                                    
                                    {/* Secondary Actions */}
                                    <div className="flex gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            className="flex-1"
                                            onClick={() => handleEditPlan(plan)}
                                        >
                                            <Edit className="h-4 w-4 mr-1" />
                                            Edit
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleDeletePlan(plan.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}

                    {/* Empty state */}
                    {plans.length === 0 && (
                        <Card className="col-span-full border-dashed border-2 border-slate-200">
                            <CardContent className="p-12 text-center">
                                <CalendarDays className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Study Plans Yet</h3>
                                <p className="text-slate-500 mb-4">Create your first study plan to organize your exam preparation</p>
                                <Button onClick={() => setShowCreateDialog(true)}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    Create Study Plan
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </div>
                    </TabsContent>

                    {/* Lecture Slides Tab */}
                    <TabsContent value="lectures" className="mt-6">
                        <LectureUpload />
                    </TabsContent>

                    {/* Scratch Pad Tab */}
                    <TabsContent value="scratchpad" className="mt-6">
                        <ScratchPads />
                    </TabsContent>
                </Tabs>

                {/* Create/Edit Dialog */}
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                        <DialogHeader className="flex-shrink-0">
                            <DialogTitle style={{ fontFamily: 'Manrope' }}>
                                {editingPlan ? 'Edit Study Plan' : 'Create Study Plan'}
                            </DialogTitle>
                            <DialogDescription>
                                Set up your exam details and study reminders
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-6 py-4 overflow-y-auto flex-1 min-h-0 pr-1">
                            {/* Plan Name */}
                            <div className="space-y-2">
                                <Label htmlFor="plan-name">Plan Name</Label>
                                <Input
                                    id="plan-name"
                                    placeholder="e.g., CBSE Spring 2026"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    data-testid="plan-name-input"
                                />
                            </div>

                            {/* Test Date */}
                            <div className="space-y-2">
                                <Label>Test Date</Label>
                                <Input
                                    type="text"
                                    placeholder="MM/DD/YYYY"
                                    value={formData.test_date_text || ""}
                                    onChange={(e) => {
                                        let val = e.target.value.replace(/[^\d/]/g, '');
                                        const raw = val.replace(/\//g, '');
                                        if (raw.length <= 8) {
                                            if (raw.length >= 3 && !val.includes('/')) {
                                                val = raw.slice(0, 2) + '/' + raw.slice(2);
                                            }
                                            if (raw.length >= 5) {
                                                val = raw.slice(0, 2) + '/' + raw.slice(2, 4) + '/' + raw.slice(4);
                                            }
                                        }
                                        let parsedDate = null;
                                        if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                                            const [mm, dd, yyyy] = val.split('/');
                                            const d = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
                                            if (!isNaN(d.getTime()) && d.getMonth() === parseInt(mm) - 1) {
                                                parsedDate = d;
                                            }
                                        }
                                        setFormData({ ...formData, test_date: parsedDate, test_date_text: val });
                                    }}
                                    className="w-full"
                                    data-testid="test-date-input"
                                />
                                {formData.test_date_text && formData.test_date_text.length === 10 && !formData.test_date && (
                                    <p className="text-xs text-red-500">Invalid date</p>
                                )}
                                {formData.test_date && (
                                    <p className="text-xs text-emerald-600">{format(formData.test_date, "PPPP")}</p>
                                )}
                            </div>

                            {/* Target Passing Score */}
                            <div className="space-y-2">
                                <Label>Target Passing Score (%)</Label>
                                <div className="flex items-center gap-3">
                                    <Input
                                        type="number"
                                        min={30}
                                        max={100}
                                        value={formData.target_score}
                                        onChange={(e) => setFormData({ ...formData, target_score: Math.min(100, Math.max(30, parseInt(e.target.value) || 70)) })}
                                        className="w-24"
                                        data-testid="target-score-input"
                                    />
                                    <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                                        <div
                                            className={cn(
                                                "h-full rounded-full transition-all",
                                                formData.target_score >= 80 ? "bg-emerald-500" :
                                                formData.target_score >= 60 ? "bg-blue-500" : "bg-amber-500"
                                            )}
                                            style={{ width: `${formData.target_score}%` }}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-slate-400">The score you need to pass. Used to calculate your exam readiness prediction.</p>
                            </div>

                            {/* Subjects */}
                            <div className="space-y-2">
                                <Label>Subjects Covered</Label>
                                <div className="flex flex-wrap gap-2">
                                    {SUBJECTS.map(subject => (
                                        <Badge
                                            key={subject}
                                            variant={formData.subjects.includes(subject) ? "default" : "outline"}
                                            className={cn(
                                                "cursor-pointer transition-all",
                                                formData.subjects.includes(subject) && "bg-indigo-500"
                                            )}
                                            onClick={() => toggleSubject(subject)}
                                        >
                                            {subject}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Organ Systems */}
                            <div className="space-y-2">
                                <Label>Organ Systems Covered</Label>
                                <div className="flex flex-wrap gap-2">
                                    {ORGAN_SYSTEMS.map(system => (
                                        <Badge
                                            key={system}
                                            variant={formData.organ_systems.includes(system) ? "default" : "outline"}
                                            className={cn(
                                                "cursor-pointer transition-all",
                                                formData.organ_systems.includes(system) && "bg-emerald-500"
                                            )}
                                            onClick={() => toggleSystem(system)}
                                        >
                                            {system}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Specific Topics */}
                            <div className="space-y-2">
                                <Label htmlFor="topics">Specific Topics (Optional)</Label>
                                <Textarea
                                    id="topics"
                                    placeholder="e.g., Cardiac arrhythmias, Renal tubular acidosis, Gram-negative bacteria..."
                                    value={formData.specific_topics}
                                    onChange={(e) => setFormData({ ...formData, specific_topics: e.target.value })}
                                    rows={3}
                                />
                            </div>

                            {/* Notifications */}
                            <div className="space-y-4 p-4 rounded-lg bg-slate-50 border border-slate-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Bell className="h-5 w-5 text-indigo-500" />
                                        <Label htmlFor="notifications" className="cursor-pointer">
                                            Study Reminders
                                        </Label>
                                    </div>
                                    <Checkbox
                                        id="notifications"
                                        checked={formData.notification_enabled}
                                        onCheckedChange={(checked) => setFormData({ ...formData, notification_enabled: checked })}
                                    />
                                </div>

                                {formData.notification_enabled && (
                                    <div className="space-y-4 pt-2">
                                        <div className="space-y-2">
                                            <Label className="text-sm">Reminder Days</Label>
                                            <div className="flex flex-wrap gap-2">
                                                {DAYS_OF_WEEK.map(day => (
                                                    <Badge
                                                        key={day}
                                                        variant={formData.notification_days.includes(day) ? "default" : "outline"}
                                                        className="cursor-pointer"
                                                        onClick={() => toggleDay(day)}
                                                    >
                                                        {day.slice(0, 3)}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="notification-time" className="text-sm">Reminder Time</Label>
                                            <div className="flex items-center gap-2">
                                                <Clock className="h-4 w-4 text-slate-400" />
                                                <Input
                                                    id="notification-time"
                                                    type="time"
                                                    value={formData.notification_time}
                                                    onChange={(e) => setFormData({ ...formData, notification_time: e.target.value })}
                                                    className="w-32"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <DialogFooter className="flex-shrink-0">
                            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                                Cancel
                            </Button>
                            <Button onClick={handleCreatePlan} data-testid="save-plan-btn">
                                {editingPlan ? 'Update Plan' : 'Create Plan'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Reward Claim Dialog */}
                <Dialog open={showRewardDialog} onOpenChange={setShowRewardDialog}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-2xl" style={{ fontFamily: 'Manrope' }}>
                                <Sparkles className="h-6 w-6 text-amber-500" />
                                Congratulations!
                            </DialogTitle>
                            <DialogDescription>
                                You've reached new milestones! Claim your rewards.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="py-6 space-y-4">
                            {rewards?.unclaimed_milestones?.map(milestone => (
                                <div 
                                    key={milestone}
                                    className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                                            <Trophy className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-900">{milestone.toLocaleString()} Questions!</p>
                                            <p className="text-sm text-slate-600">{MILESTONE_MESSAGES[milestone]?.slice(2) || 'Amazing achievement!'}</p>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() => handleClaimMilestone(milestone)}
                                        disabled={claimingMilestone === milestone}
                                        className="bg-gradient-to-r from-amber-500 to-orange-500"
                                    >
                                        {claimingMilestone === milestone ? 'Claiming...' : 'Claim'}
                                    </Button>
                                </div>
                            ))}

                            {rewards?.unclaimed_milestones?.length === 0 && (
                                <div className="text-center py-4">
                                    <PartyPopper className="h-12 w-12 mx-auto mb-3 text-amber-500" />
                                    <p className="text-slate-600">All rewards claimed! Keep studying to earn more.</p>
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowRewardDialog(false)}>
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Study Now Dialog */}
                <Dialog open={showStudyNowDialog} onOpenChange={setShowStudyNowDialog}>
                    <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
                        <DialogHeader className="flex-shrink-0">
                            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                                <Play className="h-5 w-5 text-emerald-500" />
                                Start Study Session
                            </DialogTitle>
                            <DialogDescription>
                                {selectedPlanForStudy && (
                                    <>Practice questions based on your <strong>{selectedPlanForStudy.name}</strong> plan</>
                                )}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="py-6 space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
                            {/* Focus Subject Indicator */}
                            {focusSubject && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200" data-testid="focus-subject-indicator">
                                    <Target className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                                    <p className="text-sm text-indigo-700">
                                        Focused on: <strong>{focusSubject}</strong>
                                    </p>
                                    <button onClick={() => setFocusSubject(null)} className="ml-auto text-indigo-400 hover:text-indigo-600">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            )}

                            {/* Plan Summary */}
                            {selectedPlanForStudy && (
                                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                                    <p className="text-sm font-medium text-slate-700">Session will cover:</p>
                                    {selectedPlanForStudy.subjects?.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {selectedPlanForStudy.subjects.map(s => (
                                                <Badge key={s} variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                                                    {s}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    {selectedPlanForStudy.organ_systems?.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {selectedPlanForStudy.organ_systems.map(s => (
                                                <Badge key={s} variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    {s}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    {selectedPlanForStudy.specific_topics && (
                                        <p className="text-xs text-slate-500">
                                            <span className="font-medium">Topics:</span> {selectedPlanForStudy.specific_topics}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Question Count Selection */}
                            <div className="space-y-2">
                                <Label>How many questions would you like?</Label>
                                <div className="flex flex-wrap gap-2">
                                    {QUESTION_COUNTS.map(count => (
                                        <Button
                                            key={count}
                                            type="button"
                                            variant={questionCount === count ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setQuestionCount(count)}
                                            className={cn(
                                                "min-w-[50px]",
                                                questionCount === count && "bg-emerald-500 hover:bg-emerald-600"
                                            )}
                                        >
                                            {count}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <DialogFooter className="flex-shrink-0">
                            <Button variant="outline" onClick={() => setShowStudyNowDialog(false)}>
                                Cancel
                            </Button>
                            <Button 
                                onClick={startStudySession}
                                disabled={startingSession}
                                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                                data-testid="start-session-btn"
                            >
                                {startingSession ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Starting...
                                    </>
                                ) : (
                                    <>
                                        <Play className="mr-2 h-4 w-4" />
                                        Start Session
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Abandon Session Confirmation */}
                <AlertDialog open={!!abandoningSession} onOpenChange={(open) => !open && setAbandoningSession(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Abandon Session?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will end the session and your progress won't be saved. You can always start a new session.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleAbandonSession(abandoningSession)}
                                data-testid="confirm-abandon-btn"
                            >
                                Abandon
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </Layout>
    );
}
